import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import { AsyncSelectInput } from '@/components/Forms/AsyncSelect';
import FormSection from '@/components/Forms/FormSection';
import { FlagBox } from '@/components/QueueFlags/FlagBox';
import { debounceAsync } from '@/helpers/debounceAsync';
import { formatGeneratorPowerRating } from '@/helpers/formatGeneratorPowerRating';
import usePermissions from '@/hooks/usePermissions';
import { useSearchProductsMutation } from '@/services/productCatalog';
import { Box, Button, TextInput, toast } from '@palmetto/palmetto-components';
import { useCallback, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';

import {
  GeneratorEquipmentType,
  GeneratorEquipmentTypeLabels,
  RequirementFlagSettingsType,
  UserPermissions,
} from 'types';

interface EquipmentSectionProps {
  systemIndex: number;
  isPackageEditable?: boolean;
}

const getLinkForManufacturer = (manufacturer: string): string => {
  const normalizedManufacturer = manufacturer.toLowerCase().trim();
  const warrantyLinks: Record<string, string> = {
    generac: 'https://www.generac.com/support',
  };
  return warrantyLinks[normalizedManufacturer] || '';
};

const dataKey = 'equipment' as const;
const label = 'Equipment Details' as const;

interface SerialNumberRowProps {
  systemIndex: number;
  index: number;
  serialIndex: number;
  isDisabled: boolean;
}

const SerialNumberRow = ({ systemIndex, index, serialIndex, isDisabled }: SerialNumberRowProps) => {
  const { control } = useFormContext<any>();
  const name = `systems.${systemIndex}.${dataKey}.items.${index}.serialNumbers.${serialIndex}`;
  return (
    <Controller
      control={control}
      name={name}
      rules={{ required: true }}
      render={({ field }) => (
        <TextInput
          id={name}
          name={name}
          label=""
          hideLabel
          placeholder="Serial Number"
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          width="100"
          isDisabled={isDisabled}
        />
      )}
    />
  );
};

interface EquipmentRowProps {
  systemIndex: number;
  index: number;
  isDisabled: boolean;
  isPackageEditable: boolean;
  isFinCoUser: boolean;
  searchProducts: (input: string) => Promise<any>;
}

const EquipmentRow = ({
  systemIndex,
  index,
  isDisabled,
  isPackageEditable,
  isFinCoUser,
  searchProducts,
}: EquipmentRowProps) => {
  const { control, setValue } = useFormContext<any>();
  const itemBasePath = `systems.${systemIndex}.${dataKey}.items.${index}`;
  const item:
    | {
        type: GeneratorEquipmentType;
        manufacturer?: string;
        model?: string;
        productId?: string;
        name?: string;
        isSerialNumberRequired?: boolean;
        serialNumbers?: string[];
        quantity?: number;
        lpPowerRating?: string;
        gasPowerRating?: string;
      }
    | undefined = useWatch({ control, name: itemBasePath });

  const setItemField = useCallback(
    (key: string, value: any) => {
      setValue(`${itemBasePath}.${key}`, value, { shouldDirty: true });
    },
    [setValue, itemBasePath],
  );

  const handleClearSelection = useCallback(() => {
    setItemField('manufacturer', undefined);
    setItemField('model', undefined);
    setItemField('productId', undefined);
    setItemField('name', undefined);
  }, [setItemField]);

  const handleSelectProduct = useCallback(
    (event: any) => {
      const selected = event?.target?.value;
      if (!selected || !selected.value) return;
      setValue(
        itemBasePath,
        {
          ...item,
          manufacturer: selected.manufacturer,
          model: selected.model,
          lpPowerRating: selected.lpPowerRating,
          gasPowerRating: selected.gasPowerRating,
          quantity: item?.quantity ?? 1,
          productId: selected.value,
          name: selected.name,
          isSerialNumberRequired: selected.isSerialNumberRequired ?? item?.isSerialNumberRequired,
        },
        { shouldDirty: true },
      );
    },
    [setValue, itemBasePath, item],
  );

  const handleLoadOptions = useCallback(
    async (input: string) => {
      if (!item) return [];
      const options = await searchProducts(input);
      return options ?? [];
    },
    [item, searchProducts],
  );

  const renderNoOptions = useCallback(
    ({ inputValue }: { inputValue: string | React.JSX.Element }) => {
      const equipmentLabel = item ? GeneratorEquipmentTypeLabels[item.type] : 'item';
      return inputValue ? `No ${equipmentLabel} found` : `Type to search for ${equipmentLabel}...`;
    },
    [item],
  );

  if (!item) return null;

  const equipmentTypeLabel = GeneratorEquipmentTypeLabels[item.type] || `Equipment ${index + 1}`;
  const hasProductSelected = !!(item.manufacturer && item.model);
  const powerRating = formatGeneratorPowerRating(item.lpPowerRating, item.gasPowerRating);
  const serialCount = Math.max(item.serialNumbers?.length ?? 0, item.quantity ?? 1, 1);

  return (
    <Box childGap="lg" padding="lg" background="secondary" radius="md">
      <Box fontSize="md" fontWeight="medium">
        {equipmentTypeLabel}
      </Box>

      <Box direction={{ base: 'column', tablet: 'row' }} gap="lg" alignItems={{ tablet: 'flex-start' }}>
        <Box flex="auto" minWidth="0">
          {hasProductSelected ? (
            <Box gap="sm" direction="row" alignItems="center">
              <Box gap="xs" direction="column" fontSize="sm">
                <Box fontSize="sm">
                  {item.manufacturer} - {item.model}
                  {powerRating ? ` ${powerRating}` : ''}
                </Box>
                {item.name && (
                  <Box fontSize="sm" style={{ fontStyle: 'italic' }}>
                    ({item.name})
                  </Box>
                )}
                {isFinCoUser && getLinkForManufacturer(item.manufacturer ?? '') && (
                  <Box>
                    <Link target="_blank" to={getLinkForManufacturer(item.manufacturer ?? '')}>
                      <Button size="sm" variant="secondary">
                        Warranty Registration
                      </Button>
                    </Link>
                  </Box>
                )}
              </Box>
              <Button
                iconPrefix="remove"
                variant="tertiary"
                tone="danger"
                aria-label="Clear model"
                size="sm"
                isDisabled={!isPackageEditable}
                onClick={handleClearSelection}
              >
                Clear
              </Button>
            </Box>
          ) : (
            <AsyncSelectInput
              id={`${itemBasePath}.selectedItem`}
              name={`${itemBasePath}.selectedItem`}
              label={`Add ${equipmentTypeLabel} Model`}
              loadOnOpen
              loadOptions={handleLoadOptions}
              isDisabled={isDisabled}
              onChange={handleSelectProduct}
              isClearable
              value={''}
              placeholder="Search by Model or Name..."
              noOptionsMessage={renderNoOptions}
            />
          )}
        </Box>
      </Box>

      <Box>
        <Box padding="0 0 xs 0">{`Serial Number${item.isSerialNumberRequired ? ' *' : ''}`}</Box>
        <Box gap="sm">
          {Array.from({ length: serialCount }).map((_, serialIndex) => (
            <SerialNumberRow
              key={`${itemBasePath}-serial-${serialIndex}`}
              systemIndex={systemIndex}
              index={index}
              serialIndex={serialIndex}
              isDisabled={isDisabled}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const InstallPackageEquipmentSection = ({ systemIndex, isPackageEditable = true }: EquipmentSectionProps) => {
  const permissions = usePermissions();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const isDisabled = userOnlyHasReaderPermission || !isPackageEditable;
  const { control } = useFormContext<any>();
  const { fields } = useFieldArray({
    control,
    name: `systems.${systemIndex}.${dataKey}.items`,
  });

  const [triggerSearch] = useSearchProductsMutation();

  const handleSearchText = useCallback(
    async (searchText: string) => {
      const { data, error } = await triggerSearch({
        ...(searchText ? { query: searchText } : {}),
        limit: 20,
        page: 1,
        filters: [
          {
            filter: 'classificationPath',
            operation: { equals: 'generators' },
          },
          {
            filter: 'availableOnDate',
            operation: { equals: new Date().toISOString() },
          },
        ],
      });
      if (error) {
        toast.error('Error searching products. Please try again.');
        return undefined;
      }
      const productData = data?.data?.map((product) => {
        // Catalog attribute values can be numbers; coerce to string to match the schema/form types.
        const lpPowerRating = String(
          product.attributes.find((attr) => attr.key === 'power-rating-lp-kw')?.value ?? '',
        );
        const gasPowerRating = String(
          product.attributes.find((attr) => attr.key === 'power-rating-gas-kw')?.value ?? '',
        );
        const model = product.attributes.find((attr) => attr.key === 'model-number')?.value ?? '';
        const powerRating = formatGeneratorPowerRating(lpPowerRating, gasPowerRating);
        return {
          label: `${product.manufacturer} - ${model}${powerRating ? ` ${powerRating}` : ''}`,
          value: product.id,
          manufacturer: product.manufacturer,
          model,
          lpPowerRating,
          gasPowerRating,
          productType: GeneratorEquipmentType.generator,
          name: product.name,
          isSerialNumberRequired: true,
        };
      });
      return productData?.length && (data?.paging?.totalPages || 0) > 1
        ? [
            {
              label: `${data?.paging.totalRecords} results found. Continue typing to narrow down results.`,
              value: undefined,
            },
            ...productData,
          ]
        : productData;
    },
    [triggerSearch],
  );

  const debouncedSearch = useMemo(
    () => debounceAsync((value: string) => handleSearchText(value), 400, 2, true),
    [handleSearchText],
  );

  return (
    <FormSection
      title={label}
      description={`Update applicable ${label} for the proposal.`}
      baseStyle={{
        borderWidth: '0',
      }}
    >
      <Box gap="lg">
        {fields.map((fieldItem, index) => (
          <EquipmentRow
            key={fieldItem.id}
            systemIndex={systemIndex}
            index={index}
            isDisabled={isDisabled}
            isPackageEditable={isPackageEditable}
            isFinCoUser={isFinCoUser}
            searchProducts={debouncedSearch as (input: string) => Promise<any>}
          />
        ))}

        <FlagBox
          baseCategory="equipment"
          dataKey={`Installed ${label} Issue ${systemIndex + 1}`}
          flagType={RequirementFlagSettingsType.equipmentGenerator}
        />
      </Box>
    </FormSection>
  );
};
